import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { UploadCloud, FileCheck2 } from "lucide-react";

const YEARS = ["1st Year", "2nd Year", "3rd Year", "4th Year", "5th Year", "Employee"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const FOOD_PREFS = ["Vegetarian", "Non-Vegetarian"];
const BED_SHARING = ["Single", "Double Sharing", "Triple Sharing", "Quad Sharing"];

const EMPTY = {
  surname: "",
  full_name: "",
  contact_number: "",
  kl_id: "",
  student_id_number: "",
  date_of_birth: "",
  parent_full_name: "",
  parent_contact_number: "",
  flat_number: "",
  sub_room: "",
  bed_sharing: "",
  year: "",
  branch: "",
  blood_group: "",
  food_preference: "",
  residential_address: "",
  date_of_joining: new Date().toISOString().slice(0, 10),
  total_fees: 0,
  fees_paid: 0,
  discount_amount: 0,
  payment_method: "Cash",
  payment_reference: "",
  photo_path: "",
  aadhar_path: "",
  university_id_path: "",
  email: "",
};

const TERMS = [
  "I have read and agree to the rules and regulations of Vasavi Boys Hostel.",
  "In the event of cancellation under any circumstances, the fee shall not be refundable.",
  "The above details are accurate and true to the best of my knowledge. I take full responsibility for any errors or misleading information contained therein.",
];

export default function StudentFormDialog({ open, onOpenChange, student, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [terms, setTerms] = useState([false, false, false]);
  const [feeNote, setFeeNote] = useState("");

  useEffect(() => {
    if (student) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(
          Object.keys(EMPTY).map((k) => [k, student[k] ?? EMPTY[k]])
        ),
        date_of_joining: student.date_of_joining || EMPTY.date_of_joining,
      });
      // When editing, terms are already accepted historically
      setTerms([true, true, true]);
    } else {
      setForm(EMPTY);
      setTerms([false, false, false]);
    }
    setFeeNote("");
  }, [student, open]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (field, file) => {
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      change(field, data.path);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [field]: false }));
    }
  };

  const allTermsAccepted = terms.every(Boolean);

  const submit = async () => {
    if (!student && !allTermsAccepted) {
      toast.error("Please accept all terms & conditions");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_fees: Number(form.total_fees),
        fees_paid: Number(form.fees_paid),
        discount_amount: Number(form.discount_amount || 0),
        payment_method: form.payment_method || "Cash",
        payment_reference: form.payment_reference || "",
        email: form.email || undefined,
        student_id_number: form.student_id_number || undefined,
        terms_accepted: true,
      };
      if (student) {
        // remove keys the update model doesn't accept
        const { student_id_number, terms_accepted, ...upd } = payload;
        if (feeNote) upd.fee_change_note = feeNote;
        await api.put(`/students/${student.id}`, upd);
        toast.success("Student updated");
      } else {
        await api.post("/students", payload);
        toast.success("Student enrolled");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white"
        data-testid="student-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-3xl tracking-tighter">
            {student ? "Edit Student" : "New Enrollment"}
          </DialogTitle>
          <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
            Fill in the resident's details. Fields marked * are required. All documents can be images or PDFs.
          </DialogDescription>
        </DialogHeader>

        {/* Personal */}
        <SectionTitle>Personal details</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Surname">
            <input data-testid="form-surname" value={form.surname}
              onChange={(e) => change("surname", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Student / Employee Name" required>
            <input data-testid="form-full-name" value={form.full_name}
              onChange={(e) => change("full_name", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Student / Employee Contact Number" required>
            <input data-testid="form-contact" value={form.contact_number}
              onChange={(e) => change("contact_number", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="KL ID / Admission No. / Employee ID">
            <input data-testid="form-kl-id" value={form.kl_id}
              onChange={(e) => change("kl_id", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Date of Birth">
            <input data-testid="form-dob" type="date" value={form.date_of_birth}
              onChange={(e) => change("date_of_birth", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Blood Group">
            <Select value={form.blood_group} onValueChange={(v) => change("blood_group", v)}>
              <SelectTrigger data-testid="form-blood-group" className="input-brutal font-bold">
                <SelectValue placeholder="Select blood group" />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A]">
                {BLOOD_GROUPS.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Parent */}
        <SectionTitle>Parent details</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Parent Full Name">
            <input data-testid="form-parent-name" value={form.parent_full_name}
              onChange={(e) => change("parent_full_name", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Parent Contact Number" required>
            <input data-testid="form-parent-contact" value={form.parent_contact_number}
              onChange={(e) => change("parent_contact_number", e.target.value)} className="input-brutal" />
          </Field>
        </div>

        {/* Stay */}
        <SectionTitle>Stay details</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Room Number" required>
            <input data-testid="form-flat" value={form.flat_number}
              onChange={(e) => change("flat_number", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Sub Room">
            <input data-testid="form-sub-room" value={form.sub_room}
              onChange={(e) => change("sub_room", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Bed Sharing">
            <Select value={form.bed_sharing} onValueChange={(v) => change("bed_sharing", v)}>
              <SelectTrigger data-testid="form-bed-sharing" className="input-brutal font-bold">
                <SelectValue placeholder="Select bed sharing" />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A]">
                {BED_SHARING.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Year">
            <Select value={form.year} onValueChange={(v) => change("year", v)}>
              <SelectTrigger data-testid="form-year" className="input-brutal font-bold">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A]">
                {YEARS.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Branch">
            <input data-testid="form-branch" value={form.branch}
              onChange={(e) => change("branch", e.target.value)} className="input-brutal"
              placeholder="e.g. CSE, ECE, MECH, MBA" />
          </Field>
          <Field label="Food Preference">
            <Select value={form.food_preference} onValueChange={(v) => change("food_preference", v)}>
              <SelectTrigger data-testid="form-food" className="input-brutal font-bold">
                <SelectValue placeholder="Select food preference" />
              </SelectTrigger>
              <SelectContent className="border-2 border-[#0A0A0A]">
                {FOOD_PREFS.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date of Joining" required>
            <input data-testid="form-doj" type="date" value={form.date_of_joining}
              onChange={(e) => change("date_of_joining", e.target.value)} className="input-brutal" />
          </Field>
          <Field label="Residential Address" full>
            <textarea data-testid="form-address" value={form.residential_address}
              onChange={(e) => change("residential_address", e.target.value)}
              rows={2} className="input-brutal" />
          </Field>
        </div>

        {/* Fees */}
        <SectionTitle>Fees</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Total Fees (₹)" required>
            <input data-testid="form-total-fees" type="number" min="0"
              value={form.total_fees} onChange={(e) => change("total_fees", e.target.value)}
              className="input-brutal" />
          </Field>
          <Field label="Fees Paid (₹)" required>
            <input data-testid="form-fees-paid" type="number" min="0"
              value={form.fees_paid} onChange={(e) => change("fees_paid", e.target.value)}
              className="input-brutal" />
          </Field>
          <Field label="Discount (₹)">
            <input data-testid="form-discount" type="number" min="0"
              value={form.discount_amount || 0}
              onChange={(e) => change("discount_amount", e.target.value)}
              className="input-brutal" placeholder="0" />
          </Field>
          <Field label="Student Email (for login)">
            <input data-testid="form-email" type="email" value={form.email}
              onChange={(e) => change("email", e.target.value)} className="input-brutal"
              placeholder="optional" />
          </Field>
          {student && (
            <>
              <Field label="Dues (auto-calculated)">
                <div className="input-brutal font-heading font-black bg-[#FFFCE0]" data-testid="form-dues-preview">
                  ₹{Math.max(
                    0,
                    Number(form.total_fees || 0) - Number(form.fees_paid || 0) - Number(form.discount_amount || 0),
                  ).toLocaleString()}
                </div>
              </Field>
              <Field label="Change note (shown in fee history)" full>
                <input
                  data-testid="form-fee-note"
                  value={feeNote}
                  onChange={(e) => setFeeNote(e.target.value)}
                  className="input-brutal"
                  placeholder="e.g. Nov part-payment received; annual 5% discount applied"
                />
              </Field>
            </>
          )}
        </div>

        {student && student.last_fee_change_at && (
          <p
            className="mt-2 text-xs font-bold text-[#4A4A4A]"
            data-testid="form-last-fee-change"
          >
            Last fee change: {new Date(student.last_fee_change_at).toLocaleString("en-IN")}
            {student.last_fee_change_by ? ` · by ${student.last_fee_change_by}` : ""}
          </p>
        )}

        {student && (
          <PaymentModePicker
            method={form.payment_method}
            reference={form.payment_reference}
            onMethod={(v) => {
              change("payment_method", v);
              change("payment_reference", "");
            }}
            onReference={(v) => change("payment_reference", v)}
          />
        )}

        {/* Documents */}
        <SectionTitle>Documents</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FileUploader
            label="Aadhar (photo or PDF)"
            field="aadhar_path"
            value={form.aadhar_path}
            uploading={uploading.aadhar_path}
            onFile={(f) => uploadFile("aadhar_path", f)}
            testId="form-aadhar"
          />
          <FileUploader
            label="Passport-size Photo"
            field="photo_path"
            value={form.photo_path}
            uploading={uploading.photo_path}
            onFile={(f) => uploadFile("photo_path", f)}
            testId="form-passport-photo"
            imagePreview
            accept="image/*"
          />
          <FileUploader
            label="University / Employee ID Card"
            field="university_id_path"
            value={form.university_id_path}
            uploading={uploading.university_id_path}
            onFile={(f) => uploadFile("university_id_path", f)}
            testId="form-university-id"
          />
        </div>

        {/* Terms */}
        {!student && (
          <>
            <SectionTitle>Terms &amp; Conditions <span className="text-[#FF4D4D]">*</span></SectionTitle>
            <div className="space-y-3" data-testid="form-terms">
              {TERMS.map((t, idx) => (
                <label
                  key={idx}
                  className="flex items-start gap-3 p-3 border-2 border-[#0A0A0A] rounded-xl bg-[#FFFCE0] cursor-pointer"
                >
                  <Checkbox
                    data-testid={`form-terms-${idx}`}
                    checked={terms[idx]}
                    onCheckedChange={(v) => {
                      const next = [...terms];
                      next[idx] = !!v;
                      setTerms(next);
                    }}
                    className="mt-0.5 border-2 border-[#0A0A0A] data-[state=checked]:bg-[#0A0A0A]"
                  />
                  <span className="text-sm font-medium leading-snug">
                    <span className="font-bold mr-1">{idx + 1}.</span>
                    {t}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="mt-6 gap-3">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="form-cancel"
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || (!student && !allTermsAccepted)}
            data-testid="form-save"
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
          >
            {saving ? "Saving..." : student ? "Save changes" : "Enroll student"}
          </button>
        </DialogFooter>

        <style>{`
          .input-brutal {
            width: 100%;
            padding: 0.7rem 1rem;
            border: 2px solid #0A0A0A;
            border-radius: 0.75rem;
            background: white;
            font-weight: 500;
          }
          .input-brutal:focus {
            outline: none;
            box-shadow: 0 0 0 2px white, 0 0 0 4px #0A0A0A;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ children }) {
  return (
    <h4 className="mt-6 mb-3 font-heading font-black text-lg tracking-tight uppercase">
      {children}
    </h4>
  );
}

function Field({ label, required, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
        {label} {required && <span className="text-[#FF4D4D]">*</span>}
      </span>
      {children}
    </label>
  );
}

function PaymentModePicker({ method, reference, onMethod, onReference }) {
  const MODES = [
    { key: "Cash", label: "Cash", bg: "#00CC66", fg: "#FFFFFF" },
    { key: "UPI", label: "UPI", bg: "#00E5FF", fg: "#0A0A0A" },
    { key: "Cheque", label: "Cheque", bg: "#FFE600", fg: "#0A0A0A" },
    { key: "Loan", label: "Loan", bg: "#FF4D4D", fg: "#FFFFFF" },
  ];
  const activeMode = MODES.find((m) => m.key === method) || MODES[0];
  const refConfig = {
    Cash: null,
    UPI: { label: "UTR Number", placeholder: "e.g. 306512345678", testId: "form-payment-utr" },
    Cheque: { label: "Cheque Number", placeholder: "e.g. 123456", testId: "form-payment-cheque" },
    Loan: {
      label: "Loan / Lender Details",
      placeholder: "e.g. HDFC Credila loan ref#, disbursement date, EMI plan",
      testId: "form-payment-loan",
      multiline: true,
    },
  }[activeMode.key];

  return (
    <div className="mt-4 border-2 border-[#0A0A0A] rounded-xl bg-[#FFFCE0] p-4">
      <div className="text-xs font-bold uppercase tracking-widest mb-2">
        Mode of payment (for the amount being recorded now)
      </div>
      <div className="flex flex-wrap gap-2" data-testid="payment-mode-picker">
        {MODES.map((m) => {
          const isActive = m.key === activeMode.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onMethod(m.key)}
              data-testid={`payment-mode-${m.key.toLowerCase()}`}
              className={`px-4 py-2 font-bold text-sm border-2 border-[#0A0A0A] rounded-full transition-transform ${
                isActive ? "shadow-brutal" : "bg-white hover:shadow-brutal-sm"
              }`}
              style={isActive ? { backgroundColor: m.bg, color: m.fg } : {}}
            >
              {m.label}
            </button>
          );
        })}
      </div>
      {refConfig ? (
        <div className="mt-3">
          <div className="text-xs font-bold uppercase tracking-widest mb-1.5">
            {refConfig.label} <span className="text-[#FF4D4D]">*</span>
          </div>
          {refConfig.multiline ? (
            <textarea
              value={reference || ""}
              onChange={(e) => onReference(e.target.value)}
              placeholder={refConfig.placeholder}
              rows={2}
              data-testid={refConfig.testId}
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
            />
          ) : (
            <input
              value={reference || ""}
              onChange={(e) => onReference(e.target.value)}
              placeholder={refConfig.placeholder}
              data-testid={refConfig.testId}
              className="w-full px-4 py-2.5 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
            />
          )}
          <p className="text-[11px] font-medium text-[#4A4A4A] mt-1">
            Saved on the receipt beside "Payment Method" once you record the payment.
          </p>
        </div>
      ) : (
        <p className="mt-3 text-[11px] font-medium text-[#4A4A4A]">
          No reference needed for cash payments.
        </p>
      )}
    </div>
  );
}

function FileUploader({ label, value, uploading, onFile, testId, imagePreview, accept }) {
  return (
    <div className="border-2 border-[#0A0A0A] rounded-xl bg-[#FFFCE0] p-4">
      <div className="text-xs font-bold uppercase tracking-widest mb-2">{label}</div>
      {value ? (
        <div className="flex items-center gap-2 text-xs font-bold bg-[#00CC66] text-white px-2 py-1 border-2 border-[#0A0A0A] rounded mb-2 w-fit">
          <FileCheck2 size={14} strokeWidth={2.5} /> Uploaded
        </div>
      ) : null}
      <label className="inline-flex items-center gap-2 font-bold text-sm px-3 py-2 border-2 border-[#0A0A0A] rounded-full bg-white cursor-pointer hover:shadow-brutal-sm transition-shadow">
        <UploadCloud size={14} strokeWidth={2.5} />
        {uploading ? "Uploading..." : value ? "Replace" : "Upload"}
        <input
          type="file"
          accept={accept || "image/*,application/pdf"}
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          className="hidden"
          data-testid={testId}
        />
      </label>
    </div>
  );
}
